<template>
  <div>
    <p v-if="errors.length" class="text-danger">
      <b>Please correct the following error(s):</b>
    <ul>
      <li :key="error" v-for="error in errors" class="text-danger">{{ error }}</li>
    </ul>
    </p>
    <div v-if="isLoading" class="w-100 text-center">
      <div class="spinner-border" role="status"><span class="sr-only">Loading...</span></div>
    </div>
    <div v-else>
      <div v-if="action == 'share'" class="mb-3">
        <label>Share Externally with a Magic Link</label>
        <input class="form-control" v-model="recipientEmail" placeholder="Enter recipient email...">
        <label class="mt-3">Link Expiration</label>
        <v-select :options="ttlOptions" v-model="expiresTTL" placeholder="Expire link in..." :clearable="false">
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
      </div>
      <div class="accordion" id="id-artifact-accordian">
        <div class="accordion-item">
          <h2 class="accordion-header" id="id-artifact-accordian-header">
            <button
              class="accordion-button collapsed text-dark"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#id-artifact-detail-container"
              aria-expanded="false"
              aria-controls="id-artifact-detail-container"
            >
              Artifact Details
            </button>
          </h2>
          <div
            id="id-artifact-detail-container"
            class="accordion-collapse"
            aria-labelledby="id-artifact-accordian-header"
            data-bs-parent="#id-artifact-accordian"
          >
            <div class="accordion-body">
              <div class="mb-3 row">
                <label class="bg-light p-2">Artifact ID</label>
                <div class="col-sm-9 text-dark" id="artifactId">{{ artifact.artifact_id }}</div>
              </div>
              <div class="mb-3 row">
                <label class="bg-light p-2">Content Type</label>
                <div class="col-sm-9 text-dark">{{ artifact.content_type }}</div>
              </div>
              <div class="mb-3 row">
                <label class="bg-light p-2">Size</label>
                <div class="col-sm-9 text-dark">{{ bytesToSize(artifact.size) }}</div>
              </div>
              <div class="mb-3 row">
                <label class="bg-light p-2">Checksum</label>
                <div class="col-sm-9 text-wrap ">{{ artifact.checksum }}</div>
              </div>
              <div class="mb-3 row">
                <label class="bg-light p-2 ">Created</label>
                <div class="col-sm-9 text-dark">{{ formatDate(artifact.created_at) }}</div>
              </div>
            </div>
          </div>
          <div class="modal-footer stretch-footer">
            <button
              v-if="action == 'share'"
              type="button"
              class="btn btn-info btn-rounded"
              :disabled="httpPending"
              @click.prevent="share()"
            >
              <i v-if="httpPending" class="fa fa-spinner fa-spin"></i>
              <span v-else>Share</span>
            </button>
            <button
              v-else
              type="button"
              class="btn btn-info btn-rounded"
              @click.prevent="download()"
            >
              <span>Download</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import formatter from "../../mixins/formatter";
import validator from "../../mixins/validator";
import { EventBus } from "../../events/eventBus";
import moment from "moment-timezone";
import vSelect from "vue-select";

export default {
  name: "ShareExportModal",
  mixins: [formatter, validator],
  props: {
    artifactId: String,
    action: String
  },
  computed: {
    ...mapGetters("user", ["currentToken"]),
    ...mapGetters("agency", ["currentAgencyId"])
  },
  components: { vSelect },
  data() {
    return {
      isLoading: true,
      httpPending: false,
      errors: [],
      artifact: {},
      recipientEmail: null,
      expiresTTL: { label: "1 Day", value: 86400 },
      ttlOptions: [
        { label: "15 Mins", value: 900 },
        { label: "1 Hour", value: 3600 },
        { label: "3 Hours", value: 10800 },
        { label: "6 Hours", value: 21600 },
        { label: "12 Hours", value: 43200 },
        { label: "1 Day", value: 86400 },
        { label: "3 Days", value: 259200 },
        { label: "1 Week", value: 604800 },
        { label: "2 Weeks", value: 1209600 },
        { label: "3 Week", value: 1814400 },
        { label: "1 Month", value: 2419200 },
      ]
    };
  },
  async mounted() {
    await this.fetchData();
  },
  created() {
    EventBus.$on("actionButton-click", () => {
      this.validateForm();
    });
  },
  watch: {
    async artifactId() {
      await this.fetchData();
    }
  },
  methods: {
    validateForm() {},
    formatTimestamp(t, format = "MMM D, YYYY h:mm a") {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const formattedTime = moment.utc(t, "x").tz(tz).format(format);
        return formattedTime;
      } catch (e) {
        return t;
      }
    },
    async fetchData() {
      this.isLoading = true;
      const resp = await this.$ConveyApi.artifactsApi.getArtifact({ artifactId: this.artifactId });
      this.isLoading = false;
      this.artifact = resp.data.data;
    },
    async download() {
      const uriParts = this.artifact.uri.split("/");
      const fileName = uriParts[uriParts.length - 1];

      try {
        const resp = await this.$ConveyApi.artifactsApi.downloadArtifact({ artifactId: this.artifactId });
        const signedUrl = resp.data.url;

        let element = document.createElement("a");
        element.download = fileName;
        element.href = signedUrl;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      } catch (err) {
        /* eslint-disable */
        console.error(err);
        /* eslint-enable */
        this.$toastr("error", "Error downloading artifact. Please try again.");
      }
    },
    async share() {
      this.errors = [];
      if (!this.recipientEmail || this.validateEmail(this.recipientEmail) === false) {
        this.errors.push("Please enter a valid email.");
        return;
      }

      this.httpPending = true;
      
      let reqTTL = this.expiresTTL;
      if (reqTTL !== null && reqTTL.value)
          reqTTL = reqTTL.value;

      const payload = {
          recipientEmails: [this.recipientEmail],
          ttl: reqTTL ? reqTTL : 3600
      };

      try {
        await this.$ConveyApi.artifactsApi.shareArtifact({ artifactId: this.artifactId, payload });
        this.$toastr("success", `Artifact shared! <br>An email has been sent to <br>${this.recipientEmail}`);
        this.httpPending = false;
        EventBus.$emit("close-modal");
      } catch (err) {
        this.$toastr("error", "Error sharing artifact. Please try again.");
      }
    }
  },
};
</script>

<style scoped>
textarea {
  border: none;
  overflow: auto;
  outline: none;

  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  color: #343a40 !important;

  resize: none;
  /*remove the resize handle on the bottom right*/
}

.stretch-footer {
  margin-left: -16px;
  margin-right: -16px;
  margin-bottom: -16px;
}
</style>